import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Target,
  CheckCircle,
  TrendingUp,
  ArrowLeft,
  BookOpen,
  ChevronDown,
  Code,
  Database,
  Palette,
  Globe,
  Megaphone,
  Calendar,
  ListTodo,
  Plus,
  EyeOff,
  Eye,
  FileText,
  Layout,
  Zap,
  GitBranch,
  Component,
  Puzzle,
  Wrench,
  ShoppingCart,
  Settings,
  Briefcase,
  Sparkles,
  Package,
  Award,
  Search,
  Edit,
  DollarSign,
  LineChart,
  Mail,
  Workflow,
  Clock,
  Flag,
} from 'lucide-react';
import Header from '../components/Header';
import TaskCard from '../components/TaskCard';
import EmptyState from '../components/EmptyState';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SEO from '../components/SEO';
import {
  tasksData,
  isWeekend,
  getWeekendMessage,
  validateInternEmail,
  isVipInternEmail,
  getTodayInAllFormats,
  trackMetadata
} from '../data/tasks';

const TrackTasksPage = () => {
  const { trackSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [showAllTasks, setShowAllTasks] = useState(false);
  const [visibleWeeks, setVisibleWeeks] = useState(1);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [expandedWeeks, setExpandedWeeks] = useState(new Set([1]));

  const todayFormats = getTodayInAllFormats();
  const today = todayFormats.iso;
  const programStartDate = '2026-07-06';

  // Find track by slug
  const track = useMemo(() => {
    return Object.values(trackMetadata).find(t => t.slug === trackSlug);
  }, [trackSlug]);

  const selectedTrack = track?.name || '';

  // Check authentication from navigation state or redirect
  useEffect(() => {
    if (track && selectedTrack) {
      // Check if coming from TracksPage with authentication
      if (location.state?.authenticated && location.state?.email) {
        const userEmail = location.state.email;
        const isAuthorizedForTrack = validateInternEmail(userEmail, selectedTrack);
        
        if (isAuthorizedForTrack) {
          setAuthenticatedUser({
            email: userEmail,
            track: selectedTrack,
            vip: isVipInternEmail(userEmail)
          });
        } else {
          // If trying to access another track they aren't enrolled in, redirect to tracks
          navigate('/tracks', { replace: true });
        }
      } else if (!location.state?.authenticated) {
        // No auth state in location, redirect back to tracks
        navigate('/tracks', { replace: true });
      }
    }
  }, [track, selectedTrack, location.state, navigate]);

  // Redirect if track not found
  useEffect(() => {
    if (!track && trackSlug) {
      navigate('/tracks');
    }
  }, [track, trackSlug, navigate]);

  // Function to get week content description
  const getWeekContent = (trackName, weekNumber) => {
    const weekContentMap = {
      'Frontend Development': [
        'HTML & Basic Web Development',
        'CSS Styling & Design',
        'Responsive Design & Layout',
        'JavaScript Fundamentals',
        'DOM Manipulation & Version Control',
        'Advanced JavaScript Projects',
        'React Fundamentals',
        'React Projects & Portfolio'
      ],
      'WordPress Development': [
        'WordPress Setup & Basics',
        'Elementor Page Builder',
        'Theme Development Basics',
        'Advanced Elementor & Customization',
        'WordPress Plugins & Functions',
        'E-commerce & Business Sites',
        'Dynamic Content & Functionality',
        'Portfolio & Client Projects'
      ],
      'Digital Marketing': [
        'Digital Marketing Fundamentals',
        'Social Media Strategy',
        'Search Engine Optimization',
        'Content Marketing Strategy',
        'Paid Advertising (PPC & Social Ads)',
        'Analytics & Data-Driven Marketing',
        'Email Marketing & Automation',
        'Integrated Marketing Campaign'
      ]
    };

    const trackWeeks = weekContentMap[trackName] || [];
    return trackWeeks[weekNumber - 1] || `Week ${weekNumber}`;
  };

  // Function to get week-specific learning icon
  const getWeekIcon = (trackName, weekNumber) => {
    const weekIconMap = {
      'Frontend Development': [
        FileText, Palette, Layout, Zap, GitBranch, Code, Component, Award,
      ],
      'WordPress Development': [
        Settings, Puzzle, Wrench, Sparkles, Package, ShoppingCart, Database, Briefcase,
      ],
      'Digital Marketing': [
        Globe, Megaphone, Search, Edit, DollarSign, LineChart, Mail, Workflow,
      ]
    };

    const trackIcons = weekIconMap[trackName] || [];
    return trackIcons[weekNumber - 1] || BookOpen;
  };

  const toggleWeekExpansion = (weekNumber) => {
    setExpandedWeeks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(weekNumber)) {
        newSet.delete(weekNumber);
      } else {
        newSet.add(weekNumber);
      }
      return newSet;
    });
  };

  const getCurrentActiveWeek = (tasks) => {
    if (!tasks || tasks.length === 0) return 1;

    const weekGroups = tasks.reduce((groups, task) => {
      const week = Math.ceil(task.id / 5);
      if (!groups[week]) groups[week] = [];
      groups[week].push(task);
      return groups;
    }, {});

    for (const [week, weekTasks] of Object.entries(weekGroups)) {
      if (weekTasks.some(task => task.status === 'In Progress')) {
        return parseInt(week);
      }
    }

    for (const [week, weekTasks] of Object.entries(weekGroups)) {
      if (weekTasks.some(task => task.status === 'Pending')) {
        return parseInt(week);
      }
    }

    return 1;
  };

  const handleAuthenticated = (email) => {
    const isValid = validateInternEmail(email, selectedTrack);

    if (isValid) {
      setAuthenticatedUser({ email, track: selectedTrack, vip: false });
      setIsAuthModalOpen(false);
      return { success: true };
    } else {
      return {
        success: false,
        error: `This email is not registered for the ${selectedTrack} track. Please contact your mentor if you believe this is an error.`
      };
    }
  };

  const handleLogout = () => {
    setAuthenticatedUser(null);
    // Use window.location for a hard navigation to ensure clean state
    window.location.href = '/tracks';
  };

  const handleCloseAuth = () => {
    setIsAuthModalOpen(false);
    if (!authenticatedUser) {
      navigate('/tracks');
    }
  };



  const allDisplayTasks = useMemo(() => {
    if (!selectedTrack) return [];
    const tasks = tasksData[selectedTrack] || [];

    const groupedTasks = [];
    for (let i = 0; i < tasks.length; i += 5) {
      const weekTasks = tasks.slice(i, i + 5);
      const weekNumber = Math.floor(i / 5) + 1;
      groupedTasks.push({
        week: weekNumber,
        tasks: weekTasks
      });
    }
    return groupedTasks;
  }, [selectedTrack]);

  const allTrackTasks = useMemo(() => {
    if (!selectedTrack) return [];
    return tasksData[selectedTrack] || [];
  }, [selectedTrack]);

  const canAccessAllTasks = true;
  const canAccessArchivedTasks = true;
  const displayedTasks = allTrackTasks;

  if (!track) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50 font-sans flex flex-col">
      <SEO
        title={`${selectedTrack} Tasks | HindukushSoft Internship`}
        trackName={selectedTrack}
        url={`https://internship.hindukushsoft.com/track/${trackSlug}/tasks`}
      />

      <Header />

      <main className="flex-1 pb-8 sm:pb-12">
        <div className="max-w-6xl mx-auto w-full">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="px-3 sm:px-4 md:px-6 py-3 mt-4 mx-3 sm:mx-4 md:mx-6 bg-white/88 backdrop-blur-sm border border-primary/10 rounded-xl shadow-sm"
          >
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-neutral-600 hover:text-primary transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Track Selection</span>
            </button>
          </motion.div>

          {authenticatedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {/* Overall Progress Bar */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white/88 backdrop-blur-sm border border-primary/10 rounded-2xl p-3 sm:p-4 md:p-5 mx-3 sm:mx-4 md:mx-6 mt-4 mb-4 shadow-lg shadow-primary/5"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs sm:text-sm font-semibold text-neutral-900 flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                    Overall Progress
                  </h3>
                  <span className="text-xs font-medium text-neutral-500">
                    {allTrackTasks.filter(task => task.status === 'Completed' || task.status === 'Expired').length} / {allTrackTasks.length} Tasks
                  </span>
                </div>

                <div className="relative w-full bg-neutral-100 rounded-full h-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{
                      width: `${(allTrackTasks.filter(task => task.status === 'Completed' || task.status === 'Expired').length / allTrackTasks.length) * 100}%`
                    }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full bg-primary rounded-full"
                  />
                </div>

                <div className="flex justify-between items-center mt-2 text-xs text-neutral-500">
                  <span className="truncate">Started: Jul 6</span>
                  <span className="font-semibold text-primary mx-1">
                    {Math.round((allTrackTasks.filter(task => task.status === 'Completed' || task.status === 'Expired').length / allTrackTasks.length) * 100)}%
                  </span>
                  <span className="truncate">Ends: Sep 13</span>
                </div>
              </motion.div>

              {/* Program Overview Section */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="bg-white/88 backdrop-blur-sm border border-primary/10 rounded-2xl p-3 sm:p-4 md:p-5 mx-3 sm:mx-4 md:mx-6 mb-4 shadow-lg shadow-primary/5"
              >
                <div className="flex items-center gap-2 sm:gap-3 mb-4">
                  <div className="p-2 sm:p-2.5 rounded-xl bg-primary shadow-lg shadow-primary/20 flex-shrink-0">
                    <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-sm sm:text-base md:text-lg font-semibold text-neutral-900 truncate">{selectedTrack} Internship</h2>
                    <p className="text-xs sm:text-sm text-neutral-500">Complete Training Program</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
                  <div className="bg-white border border-neutral-200 rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-xl mb-2">
                      <ListTodo className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="text-base sm:text-lg font-bold text-primary">{allTrackTasks.length}</div>
                    <div className="text-xs text-neutral-500">Total Tasks</div>
                  </div>
                  <div className="bg-white border border-neutral-200 rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-xl mb-2">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="text-base sm:text-lg font-bold text-primary">8 Weeks</div>
                    <div className="text-xs text-neutral-500">Duration</div>
                  </div>
                  <div className="bg-white border border-neutral-200 rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-secondary/15 rounded-xl mb-2">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" />
                    </div>
                    <div className="text-base sm:text-lg font-bold text-secondary">Jul 6</div>
                    <div className="text-xs text-neutral-500">Start Date</div>
                  </div>
                  <div className="bg-white border border-neutral-200 rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-primary/10 rounded-xl mb-2">
                      <Flag className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div className="text-base sm:text-lg font-bold text-primary">Sep 13</div>
                    <div className="text-xs text-neutral-500">End Date</div>
                  </div>
                  <div className="bg-white border border-neutral-200 rounded-xl p-3 sm:p-4 flex flex-col items-center justify-center col-span-2 sm:col-span-1">
                    <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-secondary/15 rounded-xl mb-2">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" />
                    </div>
                    <div className="text-base sm:text-lg font-bold text-secondary">
                      {allTrackTasks.filter(t => t.status === 'Completed').length}
                    </div>
                    <div className="text-xs text-neutral-500 text-center">
                      Completed
                      <span className="block text-[10px] text-neutral-400">
                        + {allTrackTasks.filter(t => t.status === 'Expired').length} expired
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {displayedTasks.length > 0 ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="px-3 sm:px-4 md:px-6 mb-4"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm md:text-base">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                        <h2 className="font-semibold text-neutral-900">
                          {today < programStartDate ? "Program Preview" : "All Track Tasks"}
                        </h2>
                      </div>
                      <div className="flex-1 h-px bg-neutral-200" />
                      <span className="text-xs text-neutral-500 bg-neutral-100 px-2 sm:px-3 py-1 rounded-full whitespace-nowrap">
                        {today < programStartDate ? (
                          <>Program starts {new Date(programStartDate).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}</>
                        ) : (
                          <>
                            {new Date().toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </>
                        )}
                      </span>
                    </div>
                  </motion.div>

                  {today < programStartDate ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-center mb-6 px-3 sm:px-4 md:px-6"
                    >
                      <div className="bg-white border border-neutral-200 rounded-lg p-4 sm:p-6 md:p-8">
                        <div className="text-center">
                          <div className="mx-auto w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mb-4 bg-primary/10 rounded-xl flex items-center justify-center">
                            <Calendar className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary" />
                          </div>

                          <h2 className="text-sm sm:text-base md:text-lg font-semibold mb-2 text-neutral-900">
                            Program Starting Soon
                          </h2>
                          <p className="text-xs sm:text-sm text-neutral-500 mb-4 max-w-md mx-auto">
                            Get ready for an amazing learning journey starting on July 6, 2026
                          </p>

                          <div className="inline-flex items-center gap-2 bg-secondary/10 border border-secondary/20 rounded-lg px-3 sm:px-4 py-2">
                            <Calendar className="w-4 h-4 text-secondary" />
                            <span className="text-secondary-dark font-medium text-xs sm:text-sm">Launch Date: July 6, 2026</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ) : isWeekend(today) ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-center mb-6 px-3 sm:px-4 md:px-6"
                    >
                      <div className="bg-primary rounded-2xl p-4 sm:p-6 md:p-8 shadow-xl shadow-primary/20">
                        <div className="text-2xl sm:text-3xl md:text-4xl mb-3">🎉</div>
                        <h2 className="text-base sm:text-lg md:text-xl font-bold text-white mb-2">
                          {getWeekendMessage()}
                        </h2>
                        <p className="text-white/90 text-xs sm:text-sm max-w-md mx-auto">
                          No tasks today - enjoy your well-deserved break! Come back Monday for more exciting challenges.
                        </p>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-2 sm:space-y-3 mb-6 px-3 sm:px-4 md:px-6"
                    >
                      {displayedTasks.map((task, index) => (
                        <TaskCard
                          key={index}
                          task={task}
                          index={index}
                          isPreview={today < programStartDate}
                          canAccessExpiredTasks={canAccessArchivedTasks}
                          canAccessAllTasks={canAccessAllTasks}
                        />
                      ))}
                    </motion.div>
                  )}

                  {/* Collapsible All Tasks Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    className="px-4 sm:px-6 mb-6"
                  >
                    <button
                      onClick={() => {
                        setShowAllTasks(!showAllTasks);
                        if (!showAllTasks) {
                          setVisibleWeeks(1);
                          setExpandedWeeks(new Set([1]));
                        }
                      }}
                      className="w-full flex items-center justify-between p-3 sm:p-4 bg-white/88 backdrop-blur-sm border border-primary/10 rounded-2xl hover:border-secondary/20 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="p-2 sm:p-2.5 rounded-xl bg-primary shadow-lg shadow-primary/20 flex-shrink-0">
                          <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                        <div className="text-left min-w-0">
                          <h3 className="text-xs sm:text-sm md:text-base font-semibold text-neutral-900 truncate">Curriculum Overview</h3>
                          <p className="text-xs text-neutral-500 truncate">
                            {allTrackTasks.filter(t => t.status === 'Completed' || t.status === 'Expired').length} accessible • {allTrackTasks.filter(t => t.status === 'In Progress').length} in progress • {allTrackTasks.filter(t => t.status === 'Pending' || t.status === 'Locked').length} upcoming
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-neutral-500 flex-shrink-0">
                        <span className="text-xs font-medium hidden sm:inline">
                          {showAllTasks ? 'Hide' : 'Show All'}
                        </span>
                        <motion.div
                          animate={{ rotate: showAllTasks ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
                        </motion.div>
                      </div>
                    </button>
                  </motion.div>

                  {/* Animated Tasks List */}
                  <motion.div
                    initial={false}
                    animate={{
                      height: showAllTasks ? 'auto' : 0,
                      opacity: showAllTasks ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden px-3 sm:px-4 md:px-6"
                  >
                    {showAllTasks && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4 mb-6"
                      >
                        {allDisplayTasks.slice(0, visibleWeeks).map((weekGroup, weekIndex) => (
                          <motion.div
                            key={weekGroup.week}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: weekIndex * 0.05 }}
                            className="space-y-3"
                          >
                            {/* Week Header */}
                            <div className="bg-primary rounded-2xl p-3 sm:p-4 text-white shadow-lg shadow-primary/20">
                              <div className="flex items-center justify-between gap-2 sm:gap-4">
                                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                                    {(() => {
                                      const WeekIcon = getWeekIcon(selectedTrack, weekGroup.week);
                                      return <WeekIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />;
                                    })()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 text-white/70 text-xs mb-0.5 truncate">
                                      <span className="font-medium">Week {weekGroup.week}</span>
                                      <span className="hidden sm:inline">•</span>
                                      <span className="hidden sm:inline">Days {(weekGroup.week - 1) * 5 + 1}-{Math.min(weekGroup.week * 5, 40)}</span>
                                    </div>
                                    <h3 className="text-xs sm:text-sm md:text-base font-semibold text-white truncate">
                                      {getWeekContent(selectedTrack, weekGroup.week)}
                                    </h3>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                                  <div className="text-right hidden xs:block">
                                    <div className="text-base sm:text-lg font-bold text-white">
                                      {Math.round((weekGroup.tasks.filter(t => t.status === 'Completed' || t.status === 'Expired').length / weekGroup.tasks.length) * 100)}%
                                    </div>
                                    <div className="text-xs text-white/70 hidden sm:block">Complete</div>
                                  </div>

                                  <button
                                    onClick={() => toggleWeekExpansion(weekGroup.week)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 transition-colors flex-shrink-0"
                                  >
                                    <motion.div
                                      animate={{ rotate: expandedWeeks.has(weekGroup.week) ? 180 : 0 }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <ChevronDown className="w-4 h-4 text-white" />
                                    </motion.div>
                                  </button>
                                </div>
                              </div>

                              <div className="mt-2 sm:mt-3 bg-white/20 rounded-full h-1.5 overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{
                                    width: `${(weekGroup.tasks.filter(t => t.status === 'Completed' || t.status === 'Expired').length / weekGroup.tasks.length) * 100}%`
                                  }}
                                  transition={{ duration: 0.8, delay: weekIndex * 0.1 }}
                                  className="h-full bg-white rounded-full"
                                />
                              </div>
                            </div>

                            {/* Week Tasks - Collapsible */}
                            <motion.div
                              initial={false}
                              animate={{
                                height: expandedWeeks.has(weekGroup.week) ? 'auto' : 0,
                                opacity: expandedWeeks.has(weekGroup.week) ? 1 : 0
                              }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="space-y-2 sm:space-y-3 pl-3 sm:pl-4 border-l-2 border-primary/20 pt-3 sm:pt-4 pb-2">
                                {weekGroup.tasks.map((task, taskIndex) => (
                                  <TaskCard
                                    key={task.id}
                                    task={task}
                                    index={taskIndex}
                                    isOverview={true}
                                    canAccessExpiredTasks={canAccessArchivedTasks}
                                    canAccessAllTasks={canAccessAllTasks}
                                  />
                                ))}
                              </div>
                            </motion.div>
                          </motion.div>
                        ))}

                        {/* Action Buttons Container */}
                        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                          {visibleWeeks < allDisplayTasks.length && (
                            <button
                              onClick={() => {
                                const nextWeek = visibleWeeks + 1;
                                setVisibleWeeks(prev => Math.min(prev + 1, allDisplayTasks.length));
                                setExpandedWeeks(prev => new Set([...prev, nextWeek]));
                              }}
                              className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-xl transition-all shadow-lg shadow-primary/20 hover:shadow-xl"
                            >
                              <Plus className="w-4 h-4" />
                              Show Week {visibleWeeks + 1}
                            </button>
                          )}

                          {visibleWeeks < allDisplayTasks.length && (
                            <button
                              onClick={() => {
                                setVisibleWeeks(allDisplayTasks.length);
                                const currentActiveWeek = getCurrentActiveWeek(allTrackTasks);
                                setExpandedWeeks(new Set([currentActiveWeek]));
                              }}
                              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-primary/15 text-neutral-700 text-sm font-semibold rounded-xl hover:bg-primary-50 hover:border-primary/30 transition-all"
                            >
                              <Eye className="w-4 h-4" />
                              Show All Weeks
                            </button>
                          )}

                          <button
                            onClick={() => setShowAllTasks(false)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-primary/15 text-neutral-700 text-sm font-semibold rounded-xl hover:bg-primary-50 hover:border-primary/30 transition-all"
                          >
                            <EyeOff className="w-4 h-4" />
                            Hide Curriculum
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                </>
              ) : (
                <EmptyState />
              )}
            </motion.div>
          )}
        </div>
      </main>

      <Footer />

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleCloseAuth}
        trackName={selectedTrack}
        onAuthenticated={handleAuthenticated}
      />
    </div>
  );
};

export default TrackTasksPage;
